/**
 * buildCierreTicketPlainText.ts — Líneas térmicas ESC/POS del cierre (48/32 cols).
 */

import { format } from "date-fns";
import { es } from "date-fns/locale";
import type {
  CierreCaja,
  Credito,
  MovimientoCaja,
  Venta,
} from "../types/caja.types";
import type { EmpleadoNombre } from "./getEmpleadoNombre";
import type { CierreShareDocumentKind } from "./buildCierreShareCaption";
import { buildCierreTicketModel } from "./buildCierreTicketModel";
import { gastoExternoMetodoLabel } from "./externalExpensesHelper";
import { formatMotivo, formatCierreDuration } from "./cierreHelpers";
import {
  formatCierreDateShortAmPm,
  formatCierreHeaderDateAmPm,
  formatCierreHourAmPm,
} from "./cierreCuadranteHelper";
import { loadBusinessForReceipt } from "./saleTicketEscPos";
import { fmtEmp } from "./getEmpleadoNombre";
import {
  buildVendedorNombrePorIdFromVentas,
  nombreResponsableMovimientoCaja,
} from "./cajaResponsableMovimiento";
import {
  clienteNombreAbonoCredito,
  isMovimientoAbonoCredito,
  metodoPagoAbonoCorto,
} from "./movimientoAbonoCredito";
import { CUADRE_TRANSFERENCIA_MOTIVOS } from "../constants/cuadreTransferenciaMotivos";
import { buildVentasPromocionesResumen } from "./ventaPromocionesResumen";
import { METODOS_PROPINA, type MontosPorMetodoPago } from "./propinaAllocation";
import { getDefaultPaymentMethodLabel } from "@/lib/paymentMethodsConfig";
import {
  billetesMonedasBlock,
  blank,
  centered,
  fitCell,
  gridTable,
  isNarrowTicket,
  labelForWidth,
  majorSep,
  metaRow,
  minorSep,
  moneyTicket,
  movimientoCajaCols,
  movimientoCajaEntry,
  movimientoCajaTableHeader,
  propinaEmpleadoCard,
  sectionMajor,
  sectionSub,
  table2Col,
  ticketCols,
  totalRow,
  wrapText,
} from "./ticketCierreAsciiLayout";

export interface BuildCierreTicketPlainTextInput {
  cierre: CierreCaja;
  ventas: Venta[];
  creditosSesion: Credito[];
  empleadosCatalog?: EmpleadoNombre[];
  paperWidthMm?: 58 | 80;
  variant?: CierreShareDocumentKind;
}

type MetodoPagoTotals = MontosPorMetodoPago & {
  transferencia: number;
  otros: number;
};

const ZERO: MetodoPagoTotals = {
  efectivo: 0,
  nequi: 0,
  daviplata: 0,
  tarjeta: 0,
  transferencia: 0,
  otros: 0,
};

function motivoAuditoriaDigital(motivo?: string): string {
  return (
    CUADRE_TRANSFERENCIA_MOTIVOS.find((option) => option.value === motivo)
      ?.label ??
    motivo ??
    "Sin motivo registrado"
  );
}

function sumIngresosPorMetodo(movs: MovimientoCaja[]): MetodoPagoTotals {
  const acc = { ...ZERO };
  for (const m of movs) {
    if (m.tipo !== "ingreso") continue;
    const mp = m.metodoPago;
    if (!mp) {
      acc.otros += m.monto;
      continue;
    }
    if (mp in acc) acc[mp as keyof MetodoPagoTotals] += m.monto;
    else acc.otros += m.monto;
  }
  return acc;
}

function tipoMovLabel(t: MovimientoCaja["tipo"]): string {
  const map: Record<MovimientoCaja["tipo"], string> = {
    retiro: "RETIRO",
    gasto: "GASTO",
    ingreso: "INGRESO",
    devolucion: "DEVOLUCION",
    anulacion: "ANULACION",
    abrir_caja: "ABRIR CAJA",
  };
  return map[t] ?? String(t).toUpperCase();
}

function soporteLine(m: MovimientoCaja): string | null {
  if (m.tipo !== "retiro" && m.tipo !== "gasto") return null;
  if (m.tipoSoporte === "con_factura") return "Con factura";
  if (m.tipoSoporte === "no_verificado") return "Sin verificar (pendiente)";
  return "Sin soporte";
}

function horaHHmm(iso: string): string {
  return format(new Date(iso), "HH:mm", { locale: es });
}

function variantesCredito(p: Credito["productos"][number]): string {
  if (!p.variantesSeleccionadas?.length) return "";
  return p.variantesSeleccionadas
    .map((v) => `${v.groupLabel}: ${v.optionLabel}`)
    .join(" · ");
}

function cantidadCredito(p: Credito["productos"][number]): string {
  if (p.peso != null && p.peso > 0 && p.precioPorKilo) return `${p.peso} kg`;
  return String(p.cantidad ?? 0);
}

function totalesDenominaciones(
  denominaciones: Record<number, number> | undefined,
): { billetes: number; monedas: number } {
  return Object.entries(denominaciones ?? {}).reduce(
    (totales, [denominacion, cantidad]) => {
      const subtotal = Number(denominacion) * cantidad;
      if (Number(denominacion) >= 1_000) totales.billetes += subtotal;
      else totales.monedas += subtotal;
      return totales;
    },
    { billetes: 0, monedas: 0 },
  );
}

function ingresosPorMetodoBlock(
  w: number,
  totales: MetodoPagoTotals,
  totalGeneral: number,
  subtotalLabel: string,
): string[] {
  const keys = ["efectivo", "nequi", "daviplata", "tarjeta"] as const;
  const rows = keys
    .filter((k) => totales[k] > 0)
    .map((k) => ({
      left: getDefaultPaymentMethodLabel(k),
      right: moneyTicket(totales[k]),
    }));
  const digital =
    totales.nequi +
    totales.daviplata +
    totales.tarjeta +
    totales.transferencia +
    totales.otros;
  const footer: { left: string; right: string }[] = [];
  if (totales.efectivo > 0) {
    footer.push({
      left: "Total Efectivo",
      right: moneyTicket(totales.efectivo),
    });
  }
  if (digital > 0) {
    footer.push({ left: "Total Digital", right: moneyTicket(digital) });
  }
  footer.push({
    left: subtotalLabel,
    right: `+${moneyTicket(totalGeneral)}`,
  });
  return table2Col(
    w,
    labelForWidth("Método de Pago", "Método", w),
    labelForWidth("Ingreso", "Ingreso", w),
    rows,
    { footerRows: footer },
  );
}

export function buildCierreTicketPlainText(
  input: BuildCierreTicketPlainTextInput,
): string[] {
  const {
    cierre,
    ventas,
    creditosSesion,
    empleadosCatalog,
    paperWidthMm = 80,
    variant = "cierre",
  } = input;
  const isParcial = variant === "parcial";
  const w = ticketCols(paperWidthMm);
  const biz = loadBusinessForReceipt();
  const model = buildCierreTicketModel(cierre, ventas);
  const { audit } = model;
  const lines: string[] = [];

  const documentTitle = isParcial ? "REPORTE PARCIAL" : "CIERRE DE CAJA";
  const movSorted = [...audit.movimientosActivos].sort(
    (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
  );
  const movCreditoAbonos = movSorted.filter(isMovimientoAbonoCredito);
  const movOtros = movSorted.filter((m) => !isMovimientoAbonoCredito(m));
  const ingresosManuales = movOtros.filter((m) => m.tipo === "ingreso");
  const creditoTotales = sumIngresosPorMetodo(movCreditoAbonos);
  const totalCreditoAbonos = movCreditoAbonos.reduce((s, m) => s + m.monto, 0);
  const ingresosManualesTotales = sumIngresosPorMetodo(ingresosManuales);
  const totalIngresosManuales = ingresosManuales.reduce(
    (s, m) => s + m.monto,
    0,
  );
  const nombrePorUsuarioId = buildVendedorNombrePorIdFromVentas(ventas);
  const diferencia = model.diferencia;
  const totalCobradoVentas = Object.values(model.ventasCobradas).reduce(
    (total, monto) => total + monto,
    0,
  );
  const blockMarker = (id: string) => `\u0000BLOCK:${id}`;
  const startBlock = (id: string) => lines.push(blockMarker(id));

  startBlock("header");
  lines.push(majorSep(w));
  lines.push(centered(biz.nombre.toUpperCase(), w));
  if (biz.slogan) lines.push(centered(biz.slogan.toUpperCase(), w));
  lines.push(majorSep(w));
  lines.push(centered(documentTitle, w));
  if (cierre.label) {
    for (const ln of cierre.label.split("\n")) {
      lines.push(centered(ln, w));
    }
  }
  if (cierre.puntoCajaNombre) lines.push(centered(cierre.puntoCajaNombre, w));
  lines.push(
    centered(
      formatCierreHeaderDateAmPm(cierre.cerradaEn || cierre.abiertaEn),
      w,
    ),
  );
  lines.push(blank(w));

  lines.push(
    metaRow("Apertura:", formatCierreDateShortAmPm(cierre.abiertaEn), w),
  );
  lines.push(
    metaRow(
      isParcial ? "Reporte:" : "Cierre:",
      cierre.cerradaEn
        ? formatCierreDateShortAmPm(cierre.cerradaEn)
        : formatCierreHeaderDateAmPm(new Date()),
      w,
    ),
  );
  lines.push(metaRow("Duracion:", formatCierreDuration(cierre), w));
  if (cierre.openedByName)
    lines.push(metaRow("Abrió:", cierre.openedByName, w));
  if (cierre.closedByName) {
    lines.push(
      metaRow(isParcial ? "Generó:" : "Cerró:", cierre.closedByName, w),
    );
  }
  lines.push(blank(w));

  if (!isParcial && cierre.baseAperturaConfirmacion) {
    startBlock("base-confirmation");
    sectionMajor(lines, "CONFIRMACIÓN BASE SIGUIENTE APERTURA", w);
    lines.push(
      metaRow("Decisión:", cierre.baseAperturaConfirmacion.decisionBase, w),
    );
    lines.push(
      metaRow(
        "Base usada al abrir:",
        moneyTicket(cierre.baseAperturaConfirmacion.montoApertura),
        w,
      ),
    );
    const variacion = cierre.baseAperturaConfirmacion.variacionBase;
    lines.push(
      metaRow(
        "Variación frente al cierre:",
        `${variacion >= 0 ? "+" : "-"}${moneyTicket(Math.abs(variacion))}`,
        w,
      ),
    );
    lines.push(
      metaRow(
        "Confirmada:",
        formatCierreDateShortAmPm(cierre.baseAperturaConfirmacion.confirmadaEn),
        w,
      ),
    );
    lines.push(blank(w));
  }

  startBlock("income-methods");
  sectionMajor(lines, "INGRESOS POR MÉTODO", w);
  const ventasRows = METODOS_PROPINA.map(
    (k) =>
      [k, getDefaultPaymentMethodLabel(k)] as [
        keyof MontosPorMetodoPago,
        string,
      ],
  )
    .filter(([k]) => model.ventasCobradas[k] > 0)
    .map(([k, label]) => ({
      left: label,
      right: moneyTicket(model.ventasCobradas[k]),
    }));

  const footerCanal = [
    {
      left: "Total Efectivo",
      right: moneyTicket(model.ventasCobradas.efectivo),
    },
    {
      left: "Total Digital",
      right: moneyTicket(model.totalDigitalCobrado),
    },
  ];

  lines.push(
    ...table2Col(
      w,
      labelForWidth("Método de Pago", "Método", w),
      "Total",
      ventasRows,
      {
        footerGroups: [
          [footerCanal[0], footerCanal[1]].filter((row) => {
            if (row.left.includes("Efectivo"))
              return model.ventasCobradas.efectivo > 0;
            return model.totalDigitalCobrado > 0;
          }),
          [
            {
              left: "TOTAL COBRADO EN VENTAS",
              right: moneyTicket(totalCobradoVentas),
            },
          ],
        ],
        closeBottom: false,
      },
    ),
  );
  lines.push(blank(w));

  if (!isParcial && typeof cierre.baseSiguiente === "number") {
    startBlock("next-base");
    sectionMajor(lines, "BASE PARA EL SIGUIENTE TURNO", w);
    lines.push(
      metaRow(
        "Decisión:",
        cierre.baseSiguiente > 0 ? "Se dejó base" : "No se dejó base",
        w,
      ),
      metaRow("Base reservada:", moneyTicket(cierre.baseSiguiente), w),
      metaRow(
        "Efectivo entregado al dueño:",
        moneyTicket(model.efectivoNetoEntregar),
        w,
      ),
    );
    lines.push(blank(w));
    if (cierre.baseSiguiente > 0 && cierre.baseSiguienteConteo) {
      sectionSub(lines, "COMPOSICIÓN FÍSICA DE LA BASE RESERVADA", w);
      lines.push(
        ...billetesMonedasBlock(
          w,
          cierre.baseSiguienteConteo.denominaciones,
          "Total base reservada",
          cierre.baseSiguiente,
        ),
        blank(w),
      );
    }
  }

  if (!isParcial) {
    startBlock("cash-movement");
    sectionMajor(lines, "MOVIMIENTO DEL EFECTIVO", w);
    const cajaRows: { left: string; right: string }[] = [
      { left: "Base Inicial", right: moneyTicket(cierre.montoInicial) },
      {
        left: labelForWidth(
          "+ Ventas Efectivo (Netas)",
          "+Vtas.Efec.(netas)",
          w,
        ),
        right: moneyTicket(model.ventasCobradas.efectivo),
      },
    ];
    if (audit.totalIngresosManualesEfectivo > 0) {
      cajaRows.push({
        left: labelForWidth("+ INGRESO (Mov. de Caja)", "+INGRESO (caja)", w),
        right: moneyTicket(audit.totalIngresosManualesEfectivo),
      });
    }
    if (audit.totalAbonosCreditoEfectivo > 0) {
      cajaRows.push({
        left: labelForWidth(
          "+ ABONOS A CRÉDITO (efectivo)",
          "+AB.CRÉDITO (ef.)",
          w,
        ),
        right: moneyTicket(audit.totalAbonosCreditoEfectivo),
      });
    }
    if (audit.propinasEfectivo > 0) {
      cajaRows.push({
        left: labelForWidth(
          "+ PROPINAS (Servicio · efectivo)",
          "+PROPINAS (ef.)",
          w,
        ),
        right: moneyTicket(audit.propinasEfectivo),
      });
    }
    if (audit.totalRetirosSeguridad + audit.retirosOtros > 0) {
      cajaRows.push({
        left: labelForWidth("− RETIRO (Mov. de Caja)", "−RETIRO (caja)", w),
        right: `-${moneyTicket(audit.totalRetirosSeguridad + audit.retirosOtros)}`,
      });
    }
    if (audit.totalGastos > 0) {
      cajaRows.push({
        left: labelForWidth("− GASTOS (Mov. de Caja)", "−GASTOS (caja)", w),
        right: `-${moneyTicket(audit.totalGastos)}`,
      });
    }
    if (audit.totalDevolucionesEfectivo > 0) {
      cajaRows.push({
        left: labelForWidth(
          "− DEVOLUCIÓN (Mov. de Caja)",
          "−DEVOLUC.(caja)",
          w,
        ),
        right: `-${moneyTicket(audit.totalDevolucionesEfectivo)}`,
      });
    }
    cajaRows.push({
      left: "Efectivo esperado en gaveta",
      right: moneyTicket(model.esperadoGaveta),
    });
    lines.push(
      ...table2Col(
        w,
        labelForWidth("Concepto de Flujo", "Concepto", w),
        "Valor",
        cajaRows,
        { closeBottom: false },
      ),
    );
    lines.push(blank(w));

    if (cierre.aperturaBase) {
      const ap = cierre.aperturaBase;
      startBlock("aperture-base");
      sectionMajor(
        lines,
        labelForWidth(
          "VERIFICACION BASE - APERTURA DEL TURNO",
          "VERIFICACION BASE APERTURA",
          w,
        ),
        w,
      );
      lines.push(
        metaRow(
          "Base dejada (cierre anterior):",
          ap.baseCierreAnterior === null
            ? "No se dejó base"
            : moneyTicket(ap.baseCierreAnterior),
          w,
        ),
      );
      const origenLabel =
        ap.origen === "conservada"
          ? "Continuar con la misma base"
          : ap.origen === "agregada"
            ? "Agregar más base"
            : ap.origen === "reemplazada"
              ? "Retirar o cambiar"
              : ap.origen === "manual"
                ? "Monto ingresado manualmente"
                : "Precargada (sin registro)";
      lines.push(metaRow("Opción al abrir:", origenLabel, w));
      lines.push(metaRow("Decisión:", ap.decisionBase, w));
      lines.push(
        metaRow("Base con la que abrió:", moneyTicket(ap.montoApertura), w),
      );
      if (ap.baseCierreAnterior !== null) {
        const v = ap.variacionBase;
        lines.push(
          metaRow(
            "Variación vs cierre anterior:",
            v === 0
              ? moneyTicket(0)
              : `${v > 0 ? "+" : "-"}${moneyTicket(Math.abs(v))}`,
            w,
          ),
        );
      }
      lines.push(blank(w));
      if (
        ap.baseCierreAnterior !== null &&
        (ap.variacionBase !== 0 || ap.decisionBase === "base reorganizada")
      ) {
        lines.push(
          centered(
            labelForWidth(
              "* AVISO: LA BASE NO COINCIDE CON LA DEJADA *",
              "* AVISO: BASE NO COINCIDE *",
              w,
            ),
            w,
          ),
        );
        lines.push(
          centered(`(${ap.decisionBase} - revise el efectivo físico)`, w),
        );
        lines.push(blank(w));
      }
      if (
        ap.conteoBaseApertura &&
        Object.keys(ap.conteoBaseApertura.denominaciones ?? {}).length > 0
      ) {
        sectionSub(
          lines,
          labelForWidth(
            "COMPOSICION FISICA DE LA APERTURA",
            "COMPOSICION APERTURA",
            w,
          ),
          w,
        );
        lines.push(
          ...billetesMonedasBlock(
            w,
            ap.conteoBaseApertura.denominaciones,
            "Total base apertura",
            ap.conteoBaseApertura.total,
          ),
          blank(w),
        );
      }
    }

    startBlock("cash-result");
    sectionSub(lines, "RESULTADO DEL ARQUEO", w);
    lines.push(
      ...table2Col(
        w,
        "Concepto",
        "Valor",
        [
          {
            left: labelForWidth(
              "Efectivo esperado en gaveta",
              "Efectivo esperado",
              w,
            ),
            right: moneyTicket(model.esperadoGaveta),
          },
          {
            left: "Efectivo contado",
            right: moneyTicket(model.contadoReal),
          },
        ],
        {
          footerRows: [
            {
              left:
                diferencia < 0
                  ? "FALTANTE FÍSICO DE CAJA"
                  : diferencia > 0
                    ? "SOBRANTE FÍSICO DE CAJA"
                    : "ARQUEO CUADRADO",
              right:
                diferencia === 0
                  ? moneyTicket(0)
                  : `${diferencia > 0 ? "+" : "-"}${moneyTicket(Math.abs(diferencia))}`,
            },
          ],
          closeBottom: false,
        },
      ),
    );
    lines.push(blank(w));

    if (cierre.conteoEfectivo) {
      startBlock("official-count");
      const totalesConteo = totalesDenominaciones(
        cierre.conteoEfectivo.denominaciones,
      );
      sectionMajor(
        lines,
        labelForWidth(
          "ARQUEO DE EFECTIVO (OFICIAL — CIERRE)",
          "ARQUEO EFECTIVO (OFICIAL)",
          w,
        ),
        w,
      );
      lines.push(
        ...billetesMonedasBlock(
          w,
          cierre.conteoEfectivo.denominaciones,
          "Total contado en efectivo",
          cierre.conteoEfectivo.total,
        ),
        totalRow("Total billetes", moneyTicket(totalesConteo.billetes), w),
        totalRow("Total monedas", moneyTicket(totalesConteo.monedas), w),
      );
      lines.push(blank(w));
    }

    const ciego = cierre.cierreCiegoAudit;
    {
      startBlock("blind-monitor");
      sectionSub(
        lines,
        labelForWidth(
          "MONITOR DE RIESGO — ARQUEO A CIEGAS",
          "MONITOR RIESGO — A CIEGAS",
          w,
        ),
        w,
      );
      lines.push(
        metaRow(
          "Intentos fallidos registrados:",
          `${ciego?.intentosFallidos ?? 0}/3`,
          w,
        ),
      );
      const intentos = ciego?.intentos ?? [];
      if (intentos.length) {
        lines.push(
          ...gridTable(
            w,
            isNarrowTicket(w) ? [4, 8, 20] : [5, 10, w - 17],
            ["Nro.", "Hora", "Total Declarado"],
            intentos.map((it, i) => [
              String(it.numero),
              formatCierreHourAmPm(it.registradoEn),
              moneyTicket(it.total) +
                (i === intentos.length - 1 ? " (Últ.)" : ""),
            ]),
          ),
        );
      }
      lines.push(blank(w));
      const ultimo = intentos[intentos.length - 1];
      const ultimoConteo =
        ciego?.ultimoIntentoFallidoConteo ?? ultimo?.conteoEfectivo;
      if (ultimoConteo) {
        lines.push(
          `Último intento fallido — detalle (Intento ${ultimo?.numero ?? 3})`,
        );
        lines.push(
          ...billetesMonedasBlock(
            w,
            ultimoConteo.denominaciones,
            `Total intento ${ultimo?.numero ?? 3} (fallido)`,
            ultimoConteo.total,
          ),
        );
        lines.push(blank(w));
      }
      sectionSub(lines, "Comparación con arqueo oficial:", w);
      lines.push(
        totalRow("Contado oficial (cierre)", moneyTicket(model.contadoReal), w),
      );
      if (ultimo) {
        lines.push(
          totalRow(
            `Último intento fallido (${ultimo.numero})`,
            moneyTicket(ultimo.total),
            w,
          ),
        );
        const diff = model.contadoReal - ultimo.total;
        lines.push(
          totalRow(
            "Diferencia (oficial − intento 3)",
            `${diff >= 0 ? "+" : ""}${moneyTicket(Math.abs(diff))}`,
            w,
          ),
        );
      }
      lines.push(totalRow("vs Esperado gaveta", moneyTicket(diferencia), w));
      const estadoCiego = ciego?.desbloqueadoPorLimite
        ? "Desbloqueado por límite de intentos"
        : (ciego?.intentosFallidos ?? 0) > 0
          ? "Cuadrado después de reintento"
          : "Sin intentos fallidos registrados";
      lines.push(
        totalRow(
          "Estado final",
          labelForWidth(
            estadoCiego,
            ciego?.desbloqueadoPorLimite
              ? "Desbloqueado por límite"
              : (ciego?.intentosFallidos ?? 0) > 0
                ? "Cuadrado tras reintento"
                : "Sin fallos registrados",
            w,
          ),
          w,
        ),
        totalRow("Monto legal del cierre", moneyTicket(model.contadoReal), w),
      );
      lines.push(blank(w));
    }

    {
      const promos = buildVentasPromocionesResumen(ventas);
      const tiene =
        promos.totalDescuento > 0 ||
        promos.lineasMayorista > 0 ||
        promos.lineasVip > 0 ||
        promos.puntosGanadosTurno > 0 ||
        promos.puntosCirculacion > 0 ||
        promos.premiosCanjeados.length > 0;
      if (tiene) {
        // Límite por lista para no convertir el ticket en una fila de información interminable.
        const MAX_LIST = 8;
        startBlock("promociones");
        sectionMajor(lines, "DESCUENTOS, PRECIOS ESPECIALES Y FIDELIZACION", w);
        if (promos.totalDescuento > 0) {
          lines.push(
            totalRow(
              `Descuentos aplicados (${promos.ventasConDescuento})`,
              `-${moneyTicket(promos.totalDescuento)}`,
              w,
            ),
          );
          if (promos.descuentoGlobal > 0)
            lines.push(
              metaRow(
                "  Descuento global",
                `-${moneyTicket(promos.descuentoGlobal)}`,
                w,
              ),
            );
          if (promos.descuentoItems > 0)
            lines.push(
              metaRow(
                "  Descuento por item",
                `-${moneyTicket(promos.descuentoItems)}`,
                w,
              ),
            );
          if (promos.descuentoIncentivo > 0)
            lines.push(
              metaRow(
                "  Incentivo metodo pago",
                `-${moneyTicket(promos.descuentoIncentivo)}`,
                w,
              ),
            );
        }
        if (promos.lineasMayorista > 0 || promos.lineasVip > 0) {
          if (promos.lineasMayorista > 0)
            lines.push(
              metaRow(
                `Mayoristas (${promos.lineasMayorista} lineas)`,
                moneyTicket(promos.totalMayorista),
                w,
              ),
            );
          if (promos.lineasVip > 0)
            lines.push(
              metaRow(
                `VIP (${promos.lineasVip} lineas)`,
                moneyTicket(promos.totalVip),
                w,
              ),
            );
        }
        if (promos.puntosGanadosTurno > 0) {
          lines.push(
            totalRow(
              "Puntos ganados en el turno",
              `+${promos.puntosGanadosTurno.toLocaleString("es-CO")}`,
              w,
            ),
          );
          const visiblesCli = promos.porCliente.slice(0, MAX_LIST);
          const ocultosCli = promos.porCliente.length - visiblesCli.length;
          for (const c of visiblesCli) {
            lines.push(
              metaRow(
                `  ${c.nombre}${c.codigo ? ` (${c.codigo})` : ""}`,
                `${c.puntosGanados > 0 ? "+" : ""}${c.puntosGanados.toLocaleString(
                  "es-CO",
                )}${
                  c.saldoNuevo != null
                    ? ` -> ${c.saldoNuevo.toLocaleString("es-CO")} pts`
                    : " pts"
                }`,
                w,
              ),
            );
          }
          if (ocultosCli > 0)
            lines.push(
              metaRow(
                `  ... y ${ocultosCli} cliente${ocultosCli === 1 ? "" : "s"} mas`,
                "",
                w,
              ),
            );
        }
        if (promos.puntosCirculacion > 0) {
          lines.push(
            totalRow(
              "Puntos en circulacion (al cierre)",
              `${promos.puntosCirculacion.toLocaleString("es-CO")} pts`,
              w,
            ),
          );
        }
        if (promos.premiosCanjeados.length > 0) {
          lines.push(
            totalRow(
              `Premios canjeados (${promos.premiosCanjeados.length})`,
              `-${promos.puntosCanjeadosTurno.toLocaleString("es-CO")} pts`,
              w,
            ),
          );
          const visiblesPremio = promos.premiosCanjeados.slice(0, MAX_LIST);
          const ocultosPremio =
            promos.premiosCanjeados.length - visiblesPremio.length;
          for (const p of visiblesPremio) {
            lines.push(
              metaRow(
                `  ${formatCierreHourAmPm(p.hora ?? "")} ${
                  p.cliente ? `${p.cliente}: ` : ""
                }${p.nombre}`,
                `${p.puntos.toLocaleString("es-CO")} pts`,
                w,
              ),
            );
          }
          if (ocultosPremio > 0)
            lines.push(
              metaRow(
                `  ... y ${ocultosPremio} premio${ocultosPremio === 1 ? "" : "s"} mas`,
                "",
                w,
              ),
            );
        }
        lines.push(minorSep(w));
      }
    }

    if (cierre.transferenciasVerificacion) {
      startBlock("digital-audit");
      sectionMajor(lines, "RESUMEN AUDITORÍA DIGITAL", w);
      for (const [metodo, audit] of Object.entries(
        cierre.transferenciasVerificacion.porMetodo,
      )) {
        if (!audit) continue;
        const intentos = audit.intentos ?? [];
        const confirmados =
          audit.comprobantesConfirmados ?? audit.itemsVerificados?.length ?? 0;
        const estado = audit.cuadrado
          ? "CUADRE EXACTO"
          : audit.intentosAgotados
            ? "INTENTOS AGOTADOS"
            : "AUDITORÍA PENDIENTE";
        lines.push(metaRow(audit.label || metodo, estado, w));
        lines.push(
          metaRow(
            "Sistema / declarado:",
            `${moneyTicket(audit.esperado)} / ${moneyTicket(audit.declarado ?? 0)}`,
            w,
          ),
        );
        lines.push(
          metaRow(
            "Diferencia final:",
            `${audit.diferencia >= 0 ? "+" : "-"}${moneyTicket(Math.abs(audit.diferencia))}`,
            w,
          ),
        );
        lines.push(metaRow("Intentos usados:", `${intentos.length}/3`, w));
        lines.push(
          metaRow(
            "Comprobantes confirmados:",
            `${confirmados}/${audit.cantidadEsperada}`,
            w,
          ),
        );
        lines.push(
          metaRow(
            "Valor auditado:",
            moneyTicket(audit.sumaComprobantesMarcados ?? 0),
            w,
          ),
        );
        if (audit.sumaComprobantesConProblema) {
          lines.push(
            metaRow(
              "Valor no confirmado:",
              moneyTicket(audit.sumaComprobantesConProblema),
              w,
            ),
          );
        }
        if (audit.totalTarifas) {
          lines.push(
            metaRow(
              "Cobrado / comisión:",
              `${moneyTicket(audit.totalCobrado ?? audit.esperado)} / -${moneyTicket(audit.totalTarifas)}`,
              w,
            ),
          );
          lines.push(metaRow("Neto esperado:", moneyTicket(audit.esperado), w));
        }
        for (const intento of intentos) {
          lines.push(
            metaRow(
              `Intento ${intento.numero} ${formatCierreHourAmPm(intento.registradoEn)}:`,
              `${moneyTicket(intento.declarado)} (${intento.diferencia >= 0 ? "+" : "-"}${moneyTicket(Math.abs(intento.diferencia))})`,
              w,
            ),
          );
        }
        const problemas = audit.itemsConProblema ?? [];
        for (const p of problemas) {
          lines.push(
            metaRow(
              `No confirmado ${formatCierreHourAmPm(p.fecha)} ${moneyTicket(p.monto)}:`,
              `${motivoAuditoriaDigital(p.motivo)}${p.detalle ? ` - ${p.detalle}` : ""}`,
              w,
            ),
          );
        }
        lines.push(minorSep(w));
      }
      lines.push(
        metaRow(
          "Declaración de soportes:",
          cierre.transferenciasVerificacion.declaracionAceptada
            ? "ACEPTADA"
            : "PENDIENTE",
          w,
        ),
      );
      lines.push(blank(w));
    }

    if (model.resumenOperativo.ventasActivasCount > 0) {
      startBlock("shift-summary");
      sectionSub(lines, "RESUMEN DEL TURNO", w);
      const resumenTurnoRows: { left: string; right: string }[] = [
        {
          left: "Ventas realizadas",
          right: String(model.resumenOperativo.ventasActivasCount),
        },
        {
          left: "Artículos vendidos",
          right: String(model.resumenOperativo.totalArticulosVendidos),
        },
      ];
      if (model.ventasCobradas.efectivo > 0) {
        resumenTurnoRows.push({
          left: "Total Efectivo",
          right: moneyTicket(model.ventasCobradas.efectivo),
        });
      }
      if (model.totalDigitalCobrado > 0) {
        resumenTurnoRows.push({
          left: "Total Digital",
          right: moneyTicket(model.totalDigitalCobrado),
        });
      }
      resumenTurnoRows.push({
        left: labelForWidth("Total cobrado en ventas", "Total cobrado", w),
        right: moneyTicket(totalCobradoVentas),
      });
      if (model.totalRecaudado > totalCobradoVentas) {
        resumenTurnoRows.push({
          left: labelForWidth(
            "Recaudo total (incl. ingresos/abonos)",
            "Recaudo total (incl.)",
            w,
          ),
          right: moneyTicket(model.totalRecaudado),
        });
      }
      resumenTurnoRows.push({
        left: "Propinas recibidas",
        right: moneyTicket(model.propinaTotal),
      });
      if (audit.totalIngresosManualesEfectivo > 0) {
        resumenTurnoRows.push({
          left: labelForWidth("+ INGRESO (Mov. de Caja)", "+ INGRESO", w),
          right: `+${moneyTicket(audit.totalIngresosManualesEfectivo)}`,
        });
      }
      if (audit.totalAbonosCreditoEfectivo > 0) {
        resumenTurnoRows.push({
          left: labelForWidth(
            "+ ABONOS A CRÉDITO (efectivo)",
            "+ ABONOS A CRÉDITO",
            w,
          ),
          right: `+${moneyTicket(audit.totalAbonosCreditoEfectivo)}`,
        });
      }
      if (audit.totalRetirosSeguridad + audit.retirosOtros > 0) {
        resumenTurnoRows.push({
          left: labelForWidth("− RETIRO (Mov. de Caja)", "− RETIRO", w),
          right: `-${moneyTicket(audit.totalRetirosSeguridad + audit.retirosOtros)}`,
        });
      }
      if (audit.totalGastos > 0) {
        resumenTurnoRows.push({
          left: labelForWidth("− GASTOS (Mov. de Caja)", "− GASTOS", w),
          right: `-${moneyTicket(audit.totalGastos)}`,
        });
      }
      if (audit.totalDevolucionesEfectivo > 0) {
        resumenTurnoRows.push({
          left: labelForWidth("− DEVOLUCIÓN (Mov. de Caja)", "− DEVOLUCION", w),
          right: `-${moneyTicket(audit.totalDevolucionesEfectivo)}`,
        });
      }
      lines.push(
        ...table2Col(w, "Concepto", "Valor", resumenTurnoRows, {
          closeBottom: false,
        }),
      );
      if (model.resumenOperativo.horaPicoRango) {
        lines.push(
          metaRow("Hora pico:", model.resumenOperativo.horaPicoRango, w),
        );
        lines.push(
          ` (${model.resumenOperativo.horaPicoVentasCount} ventas · ${moneyTicket(model.resumenOperativo.horaPicoMonto)} en esa hora)`.slice(
            0,
            w,
          ),
        );
      }
      {
        const labelArqueo =
          model.diferencia < 0
            ? "FALTANTE FISICO"
            : model.diferencia > 0
              ? "SOBRANTE FISICO"
              : "ARQUEO CUADRADO";
        const sign = model.diferencia > 0 ? "+" : "-";
        lines.push(
          ...table2Col(
            w,
            labelForWidth("Resultado del arqueo", "Resultado arqueo", w),
            "Valor",
            [
              {
                left: labelForWidth(
                  "Efectivo esperado en gaveta",
                  "Esperado gaveta",
                  w,
                ),
                right: moneyTicket(model.esperadoGaveta),
              },
              {
                left: labelForWidth("Efectivo contado", "Contado", w),
                right: moneyTicket(model.contadoReal),
              },
              {
                left: labelArqueo,
                right:
                  model.diferencia === 0
                    ? moneyTicket(0)
                    : `${sign}${moneyTicket(Math.abs(model.diferencia))}`,
              },
            ],
            { closeBottom: false },
          ),
        );
      }
      if (cierre.aperturaBase) {
        const apResumen = cierre.aperturaBase;
        lines.push(minorSep(w).replace(/─/g, "-"));
        lines.push(
          metaRow(
            "Base dejada (cierre anterior):",
            apResumen.baseCierreAnterior === null
              ? "No se dejó base"
              : moneyTicket(apResumen.baseCierreAnterior),
            w,
          ),
        );
        lines.push(
          metaRow(
            "Base con la que abrió:",
            moneyTicket(apResumen.montoApertura),
            w,
          ),
        );
        lines.push(metaRow("Decisión al abrir:", apResumen.decisionBase, w));
        if (apResumen.baseCierreAnterior !== null) {
          const v = apResumen.variacionBase;
          lines.push(
            metaRow(
              "Variación vs cierre anterior:",
              v === 0
                ? moneyTicket(0)
                : `${v > 0 ? "+" : "-"}${moneyTicket(Math.abs(v))}`,
              w,
            ),
          );
        }
      }
      if (model.resumenOperativo.topProductos.length) {
        startBlock("top-products");
        sectionSub(
          lines,
          labelForWidth("ARTÍCULOS MÁS VENDIDOS (TOP 5)", "TOP 5 ARTÍCULOS", w),
          w,
        );
        if (isNarrowTicket(w)) {
          for (const p of model.resumenOperativo.topProductos) {
            lines.push(
              totalRow(
                `${p.unidades}x ${fitCell(p.nombre, 14)}`,
                moneyTicket(p.subtotal),
                w,
              ),
            );
          }
          lines.push(
            totalRow(
              "Total unidades top 5",
              `${model.resumenOperativo.totalUnidadesTop5} uds`,
              w,
            ),
          );
        } else {
          lines.push(
            ...gridTable(
              w,
              [4, w - 14, 10],
              ["Cant.", "Producto", "Total"],
              model.resumenOperativo.topProductos.map((p) => [
                String(p.unidades),
                p.nombre.slice(0, w - 16),
                moneyTicket(p.subtotal),
              ]),
              [
                "Total unidades top 5",
                "",
                `${model.resumenOperativo.totalUnidadesTop5} uds`,
              ],
            ),
          );
        }
        lines.push(
          totalRow(
            "Ventas del turno",
            `${model.resumenOperativo.ventasActivasCount} v`,
            w,
          ),
        );
      }
      lines.push(blank(w));
    } // fin if ventasActivasCount > 0
  }

  if (model.propinaTotal > 0) {
    startBlock("tips");
    sectionSub(
      lines,
      labelForWidth(
        "PROPINAS COBRADAS (Servicio / Propina)",
        "PROPINAS COBRADAS",
        w,
      ),
      w,
    );
    const propRows = METODOS_PROPINA.filter((m) => model.propinas[m] > 0).map(
      (m) => ({
        left: getDefaultPaymentMethodLabel(m),
        right: moneyTicket(model.propinas[m]),
      }),
    );
    lines.push(
      ...table2Col(
        w,
        labelForWidth("Método de Pago", "Método", w),
        "Ingreso",
        propRows,
        {
          footerRows: [
            { left: "Total Propinas", right: moneyTicket(model.propinaTotal) },
          ],
          closeBottom: false,
        },
      ),
    );
    lines.push(blank(w));
  }

  if (model.rendimientoEmpleados.length) {
    startBlock("employee-performance");
    sectionMajor(
      lines,
      `RENDIMIENTO POR EMPLEADO (${model.rendimientoEmpleados.length})`,
      w,
    );
    if (isNarrowTicket(w)) {
      for (const r of model.rendimientoEmpleados) {
        lines.push(
          totalRow(
            fitCell(`${r.userName} (${r.ventasCount}v)`, 16),
            moneyTicket(r.ventasTotal),
            w,
          ),
        );
        lines.push(totalRow("  Propinas", moneyTicket(r.propinasTotal), w));
      }
      lines.push(minorSep(w));
      lines.push(
        totalRow(
          "TOTALES",
          moneyTicket(
            model.rendimientoEmpleados.reduce(
              (s, row) => s + row.ventasTotal,
              0,
            ),
          ),
          w,
        ),
      );
      lines.push(totalRow("  # Ventas", `${model.ventasActivasCount} v`, w));
      lines.push(totalRow("  Propinas", moneyTicket(model.propinaTotal), w));
    } else {
      lines.push(
        ...gridTable(
          w,
          [16, 11, 8, 13],
          ["EMPLEADO", "VENTAS", "# VENTAS", "TOTAL PROPINAS"],
          model.rendimientoEmpleados.map((r) => [
            r.userName.slice(0, 15),
            moneyTicket(r.ventasTotal),
            `${r.ventasCount} v`,
            moneyTicket(r.propinasTotal),
          ]),
          [
            "TOTALES",
            moneyTicket(
              model.rendimientoEmpleados.reduce((s, r) => s + r.ventasTotal, 0),
            ),
            `${model.ventasActivasCount} v`,
            moneyTicket(model.propinaTotal),
          ],
          { closeBottom: false },
        ),
      );
    }
    lines.push(blank(w));
  }

  if (model.propinaPorEmpleado.length) {
    startBlock("employee-tips");
    sectionMajor(lines, "DETALLE DE PROPINAS POR EMPLEADO", w);
    for (const row of model.propinaPorEmpleado) {
      lines.push(
        ...propinaEmpleadoCard(w, row.userName, row.total, row.porMetodo),
      );
    }
    lines.push(majorSep(w));
    lines.push(totalRow("TOTAL PROPINAS", moneyTicket(model.propinaTotal), w));
    lines.push(majorSep(w));
    lines.push(blank(w));
  }

  if (creditosSesion.length) {
    startBlock("credits");
    sectionMajor(
      lines,
      `CRÉDITOS OTORGADOS EN EL TURNO (${creditosSesion.length})`,
      w,
    );
    const fw = [
      Math.floor(w * 0.22),
      Math.floor(w * 0.2),
      Math.floor(w * 0.18),
      w - Math.floor(w * 0.6) - 3,
    ];
    for (const f of creditosSesion) {
      if (isNarrowTicket(w)) {
        lines.push(
          totalRow(
            fitCell(f.clienteNombre || "Cliente", 12),
            moneyTicket(f.total),
            w,
          ),
        );
        lines.push(
          metaRow(
            format(new Date(f.fecha), "dd/MM HH:mm", { locale: es }),
            fmtEmp(f.userName || f.usuarioId).slice(0, 14),
            w,
          ),
        );
      } else {
        lines.push(
          ...gridTable(
            w,
            fw,
            ["CLIENTE", "TOTAL", "FECHA", "REGISTRÓ"],
            [
              [
                (f.clienteNombre || "Cliente").slice(0, 10),
                moneyTicket(f.total),
                format(new Date(f.fecha), "dd/MM HH:mm", { locale: es }),
                fmtEmp(f.userName || f.usuarioId).slice(0, 12),
              ],
            ],
          ),
        );
      }
      for (const p of f.productos ?? []) {
        const vars = variantesCredito(p);
        lines.push(
          ` • ${p.nombre}${vars ? ` — ${vars}` : ""} × ${cantidadCredito(p)}`.slice(
            0,
            w,
          ),
        );
      }
      if (f.nota?.trim()) lines.push(`   Nota: ${f.nota.trim()}`.slice(0, w));
    }
    lines.push(minorSep(w));
    lines.push(blank(w));
  }

  if (movCreditoAbonos.length) {
    startBlock("credit-payments");
    sectionMajor(lines, `ABONOS A CRÉDITO (${movCreditoAbonos.length})`, w);
    if (isNarrowTicket(w)) {
      for (const m of movCreditoAbonos) {
        lines.push(
          totalRow(
            `${horaHHmm(m.fecha)} ${fitCell(clienteNombreAbonoCredito(m.motivo || ""), 8)}`,
            `+${moneyTicket(m.monto)}`,
            w,
          ),
        );
        lines.push(
          metaRow(
            metodoPagoAbonoCorto(m.metodoPago),
            nombreResponsableMovimientoCaja(m, {
              empleadosCatalog,
              nombrePorUsuarioIdFromVentas: nombrePorUsuarioId,
            }).slice(0, 18),
            w,
          ),
        );
      }
    } else {
      lines.push(
        ...gridTable(
          w,
          [6, 9, 12, 8, 11],
          ["HORA", "CLIENTE", "INGRESO", "METODO", "RESP."],
          movCreditoAbonos.map((m) => [
            horaHHmm(m.fecha),
            clienteNombreAbonoCredito(m.motivo || "").slice(0, 8),
            `+${moneyTicket(m.monto)}`,
            metodoPagoAbonoCorto(m.metodoPago),
            nombreResponsableMovimientoCaja(m, {
              empleadosCatalog,
              nombrePorUsuarioIdFromVentas: nombrePorUsuarioId,
            }).slice(0, 10),
          ]),
        ),
      );
    }
    lines.push(blank(w));
    sectionSub(lines, "ABONOS A CRÉDITO POR MÉTODO", w);
    lines.push(
      ...ingresosPorMetodoBlock(
        w,
        creditoTotales,
        totalCreditoAbonos,
        "Subtotal abonos a crédito",
      ),
    );
    lines.push(blank(w));
  }

  if (totalIngresosManuales > 0) {
    startBlock("manual-income");
    sectionSub(lines, "INGRESOS MANUALES POR MÉTODO", w);
    lines.push(
      ...ingresosPorMetodoBlock(
        w,
        ingresosManualesTotales,
        totalIngresosManuales,
        "Subtotal ingresos manuales",
      ),
    );
    lines.push(blank(w));
  }

  if (!isParcial) {
    startBlock("cash-delivery");
    sectionSub(lines, "DINERO A ENTREGAR", w);
    lines.push(
      ...table2Col(
        w,
        "Concepto",
        "Valor",
        [
          {
            left: labelForWidth(
              "Total contado en efectivo",
              "Contado en ef.",
              w,
            ),
            right: moneyTicket(model.contadoReal),
          },
          {
            left: labelForWidth(
              (cierre.baseSiguiente ?? 0) > 0
                ? "(−) Base elegida (Se queda)"
                : "Base siguiente (No se dejó)",
              (cierre.baseSiguiente ?? 0) > 0
                ? "(−) Base elegida"
                : "Base siguiente $0",
              w,
            ),
            right:
              (cierre.baseSiguiente ?? 0) > 0
                ? `-${moneyTicket(cierre.baseSiguiente ?? 0)}`
                : moneyTicket(0),
          },
          ...(audit.propinasEfectivo > 0
            ? [
                {
                  left: labelForWidth(
                    "(−) Propinas efect. (Apartar)",
                    "(−) Propinas ef.",
                    w,
                  ),
                  right: `-${moneyTicket(audit.propinasEfectivo)}`,
                },
              ]
            : []),
        ],
        {
          footerRows: [
            {
              left: labelForWidth(
                "EFECTIVO NETO A ENTREGAR",
                "EFEC. NETO ENTREGAR",
                w,
              ),
              right: moneyTicket(model.efectivoNetoEntregar),
            },
          ],
          closeBottom: false,
        },
      ),
    );
    lines.push(blank(w));

    startBlock("turn-revenue");
    sectionSub(lines, "RECAUDO TOTAL DEL TURNO", w);
    lines.push(" RECAUDO DEL TURNO — EFECTIVO");
    lines.push(
      ...table2Col(
        w,
        " ",
        " ",
        [
          {
            left: labelForWidth(
              audit.propinasEfectivo > 0
                ? "Ventas (Incluye Propina)"
                : "Ventas",
              audit.propinasEfectivo > 0 ? "Vtas (c/propina)" : "Ventas",
              w,
            ),
            right: moneyTicket(audit.ventasEfectivoRecaudo),
          },
          ...(audit.totalIngresosManualesEfectivo > 0
            ? [
                {
                  left: labelForWidth(
                    "(+) INGRESO (Mov. de Caja)",
                    "(+) INGRESO (caja)",
                    w,
                  ),
                  right: moneyTicket(audit.totalIngresosManualesEfectivo),
                },
              ]
            : []),
          ...(audit.totalRetirosSeguridad + audit.retirosOtros > 0
            ? [
                {
                  left: labelForWidth(
                    "(+) RETIRO (Mov. de Caja)",
                    "(+) RETIRO (caja)",
                    w,
                  ),
                  right: moneyTicket(
                    audit.totalRetirosSeguridad + audit.retirosOtros,
                  ),
                },
              ]
            : []),
          ...(audit.totalAbonosCreditoEfectivo > 0
            ? [
                {
                  left: "(+) Abonos a crédito",
                  right: moneyTicket(audit.totalAbonosCreditoEfectivo),
                },
              ]
            : []),
        ],
        {
          footerRows: [
            {
              left: "TOTAL EFECTIVO",
              right: moneyTicket(audit.recaudoEfectivo),
            },
          ],
          closeBottom: false,
        },
      ),
    );
    lines.push(blank(w));
    lines.push(" RECAUDO DEL TURNO — DIGITAL");
    lines.push(
      ...table2Col(
        w,
        " ",
        " ",
        [
          {
            left: labelForWidth(
              audit.ventasDigitalRecaudo > audit.totalVentasDigitalNetas
                ? "Ventas (Incluye Propina)"
                : "Ventas",
              audit.ventasDigitalRecaudo > audit.totalVentasDigitalNetas
                ? "Vtas (c/propina)"
                : "Ventas",
              w,
            ),
            right: moneyTicket(audit.ventasDigitalRecaudo),
          },
          ...(audit.totalIngresosManualesDigital > 0
            ? [
                {
                  left: labelForWidth(
                    "(+) INGRESO (Mov. de Caja)",
                    "(+) INGRESO (caja)",
                    w,
                  ),
                  right: moneyTicket(audit.totalIngresosManualesDigital),
                },
              ]
            : []),
          ...(audit.totalAbonosCreditoDigital > 0
            ? [
                {
                  left: "(+) Abonos a crédito",
                  right: moneyTicket(audit.totalAbonosCreditoDigital),
                },
              ]
            : []),
        ],
        {
          footerRows: [
            { left: "TOTAL DIGITAL", right: moneyTicket(audit.recaudoDigital) },
          ],
          closeBottom: false,
        },
      ),
    );
    lines.push(blank(w));
    lines.push(
      totalRow(
        "TOTAL RECAUDADO DEL NEGOCIO",
        moneyTicket(model.totalRecaudado),
        w,
      ),
    );
    lines.push(blank(w));

    startBlock("financial-summary");
    sectionSub(lines, "RESUMEN FINANCIERO", w);
    const consolRows: { left: string; right: string }[] = [
      {
        left: labelForWidth("TOTAL RECAUDADO DEL TURNO", "TOTAL RECAUDADO", w),
        right: moneyTicket(model.totalRecaudado),
      },
    ];
    if (audit.totalGastos > 0) {
      consolRows.push({
        left: "(−) TOTAL GASTOS TURNO",
        right: `-${moneyTicket(audit.totalGastos)}`,
      });
    }
    if (audit.totalDevoluciones > 0) {
      consolRows.push({
        left: "(−) TOTAL DEVOLUCIONES",
        right: `-${moneyTicket(audit.totalDevoluciones)}`,
      });
    }
    lines.push(
      ...table2Col(w, "Descripción", "Valor", consolRows, {
        footerRows: [
          {
            left: labelForWidth(
              "DINERO ESPERADO AL CIERRE",
              "DINERO ESP. CIERRE",
              w,
            ),
            right: moneyTicket(model.dineroEsperadoCierre),
          },
          {
            left: labelForWidth(
              "DINERO ENCONTRADO AL CIERRE",
              "DINERO ENCONTRADO",
              w,
            ),
            right: moneyTicket(model.dineroEncontradoCierre),
          },
          ...(model.diferenciaFinancieraCierre !== 0
            ? [
                {
                  left: labelForWidth(
                    model.diferenciaFinancieraCierre < 0
                      ? "FALTANTE TOTAL DEL NEGOCIO"
                      : "SOBRANTE TOTAL DEL NEGOCIO",
                    model.diferenciaFinancieraCierre < 0
                      ? "FALTANTE NEGOCIO"
                      : "SOBRANTE NEGOCIO",
                    w,
                  ),
                  right: `${model.diferenciaFinancieraCierre >= 0 ? "+" : "-"}${moneyTicket(Math.abs(model.diferenciaFinancieraCierre))}`,
                },
              ]
            : []),
        ],
        closeBottom: false,
      }),
    );
    lines.push(blank(w));

    if (model.totalGastosExternosDia > 0) {
      startBlock("external-expenses");
      sectionSub(lines, `GASTOS DEL DUENO (FUERA DE CAJA · DIA)`, w);
      for (const g of model.gastosExternosDia) {
        const concepto = `${g.categoria}${g.descripcion ? ` · ${g.descripcion}` : ""}`;
        const metodo = gastoExternoMetodoLabel(g.metodoPago, g.metodoPagoLabel);
        lines.push(
          ...table2Col(w, "Concepto", "Monto", [
            {
              left: labelForWidth(`${concepto} (${metodo})`, concepto, w),
              right: `-${moneyTicket(g.monto)}`,
            },
          ]),
        );
      }
      lines.push(
        totalRow(
          "Total gastos del dueno (dia)",
          `-${moneyTicket(model.totalGastosExternosDia)}`,
          w,
        ),
      );
      lines.push(...wrapText("No afectan arqueo ni gaveta.", w));
      lines.push(blank(w));
    }
  }

  if (movOtros.length) {
    startBlock("movement-summary");
    sectionSub(lines, `MOVIMIENTOS (${movOtros.length})`, w);
    const tipos: MovimientoCaja["tipo"][] = [
      "retiro",
      "gasto",
      "ingreso",
      "devolucion",
      "anulacion",
      "abrir_caja",
    ];
    const resumenMovimientos = tipos.flatMap((tipo) => {
      const movimientosTipo = movOtros.filter(
        (movimiento) => movimiento.tipo === tipo,
      );
      if (!movimientosTipo.length) return [];
      const total = movimientosTipo.reduce(
        (acumulado, movimiento) => acumulado + movimiento.monto,
        0,
      );
      return [
        {
          left: `${tipoMovLabel(tipo)} (${movimientosTipo.length})`,
          right:
            tipo === "abrir_caja"
              ? "GAVETA"
              : `${tipo === "ingreso" ? "+" : "−"}${moneyTicket(total)}`,
        },
      ];
    });
    lines.push(...table2Col(w, "Tipo", "Total", resumenMovimientos), blank(w));
  }

  if (movOtros.length) {
    startBlock("cash-log");
    sectionMajor(lines, `MOVIMIENTOS DE CAJA (${movOtros.length})`, w);
    const movCols = movimientoCajaCols(w);
    lines.push(...movimientoCajaTableHeader(w, movCols));
    for (const m of movOtros) {
      const ingreso = m.tipo === "ingreso";
      const esApertura = m.tipo === "abrir_caja";
      const detalle: string[] = [];
      if (m.motivo) detalle.push(...wrapText(m.motivo, w - 2));
      const sop = soporteLine(m);
      if (sop) detalle.push(`Soporte: ${sop}`);
      if (m.tipo === "ingreso" && m.metodoPago) {
        const modo = m.metodoPago === "efectivo" ? "Efectivo" : "Digital";
        detalle.push(`Medio: ${modo} (${m.metodoPago})`);
      }
      if (m.tipo === "devolucion" && m.metodoPago) {
        const medio = m.metodoPago === "efectivo" ? "Efectivo" : "Digital";
        detalle.push(`Medio: ${medio} (${m.metodoPago})`);
        if ((m as { diferenciaMetodo?: boolean }).diferenciaMetodo) {
          detalle.push("⚠ Salida distinta al cobro original");
        }
      }
      const respNombre = nombreResponsableMovimientoCaja(m, {
        empleadosCatalog,
        nombrePorUsuarioIdFromVentas: nombrePorUsuarioId,
      });
      lines.push(
        ...movimientoCajaEntry(
          w,
          movCols,
          tipoMovLabel(m.tipo).slice(0, isNarrowTicket(w) ? 7 : 9),
          esApertura
            ? "GAVETA"
            : `${ingreso ? "+" : "-"}${moneyTicket(m.monto)}`,
          horaHHmm(m.fecha),
          fitCell(respNombre, isNarrowTicket(w) ? 9 : 18),
          detalle,
        ),
      );
    }
    lines.push(blank(w));
  }

  if (
    !isParcial &&
    (diferencia !== 0 || cierre.extraNote) &&
    (cierre.motivoDescuadre || cierre.notaDescuadre || cierre.extraNote)
  ) {
    startBlock("observation");
    sectionMajor(lines, "OBSERVACIÓN", w);
    if (cierre.motivoDescuadre) {
      lines.push(` Motivo: ${formatMotivo(cierre.motivoDescuadre)}`);
    }
    if (cierre.notaDescuadre) {
      for (const ln of wrapText(cierre.notaDescuadre, w - 1)) {
        lines.push(` ${ln}`);
      }
    }
    if (cierre.extraNote) {
      lines.push(" Mensaje adicional:");
      for (const ln of wrapText(cierre.extraNote, w - 1)) {
        lines.push(` ${ln}`);
      }
    }
    lines.push(minorSep(w).replace(/─/g, "-"));
    lines.push(
      centered(`Responsable: ${cierre.closedByName || cierre.userId}`, w),
    );
    lines.push(centered(`ID: ${cierre.id.slice(0, 14).toUpperCase()}`, w));
  }

  startBlock("footer");
  lines.push(minorSep(w).replace(/─/g, "-"));
  lines.push(centered("Gestionado con", w));
  lines.push(centered("ALTENOT", w));
  lines.push(
    centered(
      labelForWidth(
        "Imagen no editable — comprobante oficial",
        "No editable — comprobante",
        w,
      ),
      w,
    ),
  );
  lines.push(majorSep(w));

  const order = [
    "header",
    "base-confirmation",
    "shift-summary",
    "top-products",
    "promociones",
    "income-methods",
    "cash-movement",
    "official-count",
    "blind-monitor",
    "cash-result",
    "observation",
    "manual-income",
    "cash-delivery",
    "aperture-base",
    "next-base",
    "turn-revenue",
    "financial-summary",
    "external-expenses",
    "movement-summary",
    "cash-log",
    "credits",
    "credit-payments",
    "tips",
    "employee-performance",
    "employee-tips",
    "digital-audit",
    "footer",
  ];
  const blocks = new Map<string, string[]>();
  let currentBlock = "header";
  for (const line of lines) {
    if (line.startsWith("\u0000BLOCK:")) {
      currentBlock = line.slice("\u0000BLOCK:".length);
      if (!blocks.has(currentBlock)) blocks.set(currentBlock, []);
      continue;
    }
    const block = blocks.get(currentBlock) ?? [];
    block.push(line);
    blocks.set(currentBlock, block);
  }

  return order
    .flatMap((id) => blocks.get(id) ?? [])
    .map((line) => (line.length > w ? line.slice(0, w) : line));
}
